import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { supabase, type Profile } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface SignInResult {
  error: string | null;
  profile: Profile | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  mfaVerified: boolean;
  setMfaVerified: (v: boolean) => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

function isRefreshTokenError(err: unknown): boolean {
  if (err instanceof Error) {
    const msg = err.message || '';
    return msg.includes('Invalid Refresh Token') || msg.includes('Refresh Token Not Found') || msg.includes('refresh_token');
  }
  return false;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);

  // Track in-flight profile fetches to prevent concurrent calls for same userId
  const fetchInFlight = useRef<Map<string, Promise<Profile | null>>>(new Map());

  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    // Deduplicate concurrent calls for the same userId
    const existing = fetchInFlight.current.get(userId);
    if (existing) return existing;

    const promise = _doFetchProfile(userId);
    fetchInFlight.current.set(userId, promise);

    try {
      return await promise;
    } finally {
      fetchInFlight.current.delete(userId);
    }
  }, []);

  const _doFetchProfile = async (userId: string, retries = 2): Promise<Profile | null> => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        if (!supabase) {
          console.warn('[useAuth] fetchProfile: supabase client is not available');
          return null;
        }

        const result = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (!result) {
          console.warn('[useAuth] fetchProfile: supabase query returned undefined result');
          // Retry on undefined result — likely transient client issue
          if (attempt < retries) {
            await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
            continue;
          }
          return null;
        }

        const { data, error } = result;

        if (error) {
          console.error('[useAuth] fetchProfile error:', error.message, error.code, error.details);
          if (attempt < retries) {
            await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
            continue;
          }
          return null;
        }

        return data as Profile | null;
      } catch (err) {
        console.error('[useAuth] fetchProfile exception (attempt ' + (attempt + 1) + '):', err);
        if (attempt < retries) {
          await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
        }
      }
    }
    return null;
  };

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    setProfileLoading(true);
    const p = await fetchProfile(user.id);
    if (p) setProfile(p);
    setProfileLoading(false);
  }, [user, fetchProfile]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    supabase.auth.getSession().then((result) => {
      if (!mounted) return;

      const currentSession = result?.data?.session ?? null;
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      if (currentSession?.user) {
        fetchProfile(currentSession.user.id).then((p) => {
          if (!mounted) return;
          if (p) setProfile(p);
          else console.warn('[useAuth] No profile found for user:', currentSession.user.id);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    }).catch((err) => {
      if (!mounted) return;
      console.warn('[useAuth] getSession failed:', err instanceof Error ? err.message : err);

      // Stale/invalid refresh token — nuke local session so future loads don't retry the dead token
      if (isRefreshTokenError(err)) {
        console.log('[useAuth] Detected stale refresh token, clearing local session');

        // Manually wipe Supabase auth keys from localStorage as a safety net
        try {
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('sb-')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((k) => localStorage.removeItem(k));
        } catch {
          // localStorage might be unavailable in some environments
        }

        supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      }

      setSession(null);
      setUser(null);
      setProfile(null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!mounted) return;
      console.log('[useAuth] onAuthStateChange:', _event, newSession?.user?.email);

      // SIGNED_OUT can be triggered when refresh fails — clean slate
      if (_event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        setMfaVerified(false);
        setLoading(false);
        return;
      }

      // TOKEN_REFRESHED is normal; USER_UPDATED / INITIAL_SESSION are fine
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        fetchProfile(newSession.user.id).then((p) => {
          if (!mounted) return;
          if (p) setProfile(p);
          else console.warn('[useAuth] No profile in onAuthStateChange for:', newSession.user.email);
        });
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    console.log('[useAuth] signIn attempt:', email);

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('[useAuth] signIn auth error:', error.message);
      return { error: error.message, profile: null };
    }

    if (data.user) {
      console.log('[useAuth] signIn success, fetching profile for:', data.user.id);
      const p = await fetchProfile(data.user.id);
      if (p) {
        console.log('[useAuth] signIn profile loaded, role:', p.role);
        setProfile(p);
      } else {
        console.warn('[useAuth] signIn: auth OK but NO profile found for', email);
        setProfile(null);
      }
      return { error: null, profile: p };
    }

    return { error: 'Sign in failed — no user returned', profile: null };
  }, [fetchProfile]);

  const signOut = useCallback(async () => {
    console.log('[useAuth] signOut');

    // Log the logout before clearing the session
    if (user?.email) {
      const funcurl = import.meta.env.VITE_PUBLIC_SUPABASE_URL + '/functions/v1';
      fetch(`${funcurl}/manage-login-attempts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'logout', email: user.email }),
      }).catch(() => {});
    }

    // Use scope: 'global' to invalidate the refresh token server-side, then clear local
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch {
      // Network error or already invalid — just clear locally
      await supabase.auth.signOut({ scope: 'local' });
    }

    setUser(null);
    setSession(null);
    setProfile(null);
    setMfaVerified(false);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, profileLoading, signIn, signOut, refreshProfile, mfaVerified, setMfaVerified }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}